import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { getOutboundShipmentForInvoice } from "../../../graphql/outbound/custom-queries";
import { syncableInvoiceCreateProps } from "../../../graphql/invoice/invoiceCRUD";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { Button, useListContext, useNotify, useRedirect } from "react-admin";

import {
  isAuthorized,
  isSubscriptionDisabled,
  logError,
} from "../../../utils/appUtils";
import {
  AclType,
  CarrierType,
  InvoicingMode,
  MAX_INVOICE_LINE_ITEMS,
  OutboundStatus,
} from "../../../utils/constants";
import { deepRedirect, formatCurrency } from "../../../utils/utils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
  sanitizeZapierData,
  transformZapierInvoiceData,
  triggerEvent,
} from "../../../utils/zapierUtils";

import {
  AddAllIcon,
  AddIcon,
  CancelIcon,
  InvoiceIcon,
  OKIcon,
} from "../../../components/common/iconography";

import { ChargeEvent } from "../accounts/chargeRules/chargeRuleConstants";
import {
  computeAggregatedShipmentFields,
  createInvoiceBase,
  createInvoiceLambda,
  createInvoiceServiceLambda,
  createLambdaInvoiceServices,
  createTransform,
  fetchAccountMap,
  fetchReloadYard,
  findOversizedInvoice,
  getTotalInvoiced,
  isInvoiced,
  logInvoiceCreateHistory,
  setInvoiceSyncFailed,
  setInvoiceSyncPending,
  setupOutboundILineItems,
  setupOutboundInvoice,
  updateLambdaOutbound,
} from "../invoices/InvoiceUtils";
import { hasOnPullStorageRule } from "../accounts/chargeRules/ChargeRuleUtils";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

/**
 * Outbound is invoicable when RELEASED, not skipped, and has data to bill:
 * - Quantity: shipmentLineItems with totalQuantityToPick
 * - Weight: scale (tare + laden + weightUnits) or netWeight + weightUnits (schema: OutboundShipment)
 * Billing UOM comes from charge rules.
 */
const isCollected = (record) =>
  Boolean(record?.collectedDateTime || record?.isCollected);

const isInvoicable = (record) => {
  if (
    !record ||
    record.status !== OutboundStatus.RELEASED ||
    record.skipInvoice
  )
    return false;

  // Do not return false if account has no billing rules
  // users can create Accesorial Charges invoices manually for each shipment
  const billingAccountId = record.billToAccountId || record.accountId;
  if (
    hasOnPullStorageRule(billingAccountId) &&
    record.carrierType === CarrierType.RAIL &&
    !isCollected(record)
  ) {
    return false;
  }

  const hasQuantity =
    Boolean(record.totalUOM) &&
    record.shipmentLineItems?.items?.length > 0 &&
    record.shipmentLineItems.items.some(
      (sli) =>
        sli.totalQuantityToPick != null && sli.totalQuantityToPick !== "",
    );
  const hasWeight =
    (record.tareWeight != null &&
      record.ladenWeight != null &&
      record.weightUnits) ||
    (record.netWeight != null && record.weightUnits);
  return Boolean(hasQuantity || hasWeight);
};

const fetchOutboundMap = async (selectedIds) => {
  const outboundMap = {};
  const promiseList = [];

  selectedIds.forEach((outboundId) => {
    promiseList.push(
      API.graphql(
        graphqlOperation(getOutboundShipmentForInvoice, { id: outboundId }),
      ),
    );
  });

  const results = await Promise.all(promiseList);

  results.forEach((res) => {
    const outbound = res.data?.getOutboundShipment;
    if (outbound && isInvoicable(outbound) && !isInvoiced(outbound)) {
      outboundMap[outbound.id] = outbound;
    }
  });

  return outboundMap;
};

export const CreateInvoiceButton = ({ backPath, record, ...props }) => {
  const history = useHistory();
  const isDisabled = isSubscriptionDisabled();

  const handleCreate = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const billingAccountId = record.billToAccountId || record.accountId;
    deepRedirect(history, "/invoices/create", backPath, {
      event: ChargeEvent.OUTBOUND,
      accountId: billingAccountId,
      outboundShipmentId: record.id,
    });
  };

  const handleOpen = (event) => {
    debugger;
    event.stopPropagation();
    event.preventDefault();
    const invoiceId = record?.invoice?.id || (record?.invoiceId || "").trim();
    if (!invoiceId) return;
    deepRedirect(history, `/invoices/${invoiceId}`, backPath, {});
  };

  if (!record || !isInvoicable(record) || !isAuthorized(AclType.INVOICES))
    return null;
  if (isInvoiced(record)) {
    const total = formatCurrency(getTotalInvoiced(record));

    return (
      <Button label={total} color="primary" onClick={handleOpen}>
        <InvoiceIcon />
      </Button>
    );
  }
  return (
    <Button
      label="Invoice"
      color="primary"
      onClick={handleCreate}
      disabled={isDisabled}
    >
      <AddIcon />
    </Button>
  );
};

/**
 * PER_SHIPMENT mode: one invoice per inbound
 */
function generateInvoicesPerShipment(
  outboundMap,
  accountMap,
  prefix,
  counterStart,
) {
  const invoiceCreateList = [];
  const invoiceServiceCreateMap = {};
  const outboundUpdateMap = {};
  let counter = counterStart;

  Object.values(outboundMap).forEach((outbound) => {
    if (!isInvoicable(outbound) || isInvoiced(outbound)) return;

    counter++;
    // Generate base invoice
    const billingAccountId = outbound.billToAccountId || outbound.accountId;
    const account = accountMap[billingAccountId];

    const baseInvoice = setupOutboundInvoice(
      account,
      outbound,
      account.chargeRules,
      account.services,
    );
    const invoiceInput = createInvoiceLambda(baseInvoice);

    // create lineItems to calc totals on transform - needs invoiceId
    const lineItems = createLambdaInvoiceServices(invoiceInput);
    invoiceInput.lineItems = { items: lineItems };

    // transform & sanitize
    const transformedInput = createTransform(invoiceInput, prefix, counter); // TODO: pass isSyncEnabled
    invoiceCreateList.push(transformedInput);
    invoiceServiceCreateMap[invoiceInput.id] = lineItems;
    outboundUpdateMap[invoiceInput.id] = updateLambdaOutbound(
      outbound,
      baseInvoice,
    );
  });

  return {
    invoiceCreateList,
    invoiceServiceCreateMap,
    outboundUpdateMap,
    counter,
  };
}

/**
 * BULK mode: one invoice per account, grouped by shipment
 */
function generateInvoicesBulk(outboundMap, accountMap, prefix, counterStart) {
  const invoiceCreateList = [];
  const invoiceServiceCreateMap = {};
  const outboundUpdateMap = {};
  let counter = counterStart;

  Object.entries(accountMap).forEach(([accountId, account]) => {
    const relatedOutbounds = Object.values(outboundMap).filter(
      (outbound) =>
        (outbound.billToAccountId || outbound.accountId) === accountId &&
        isInvoicable(outbound) &&
        !isInvoiced(outbound),
    );

    if (relatedOutbounds.length === 0) return;

    counter++;
    const baseInvoice = {
      ...createInvoiceBase(account, null, null),
      event: ChargeEvent.OUTBOUND,
    };
    const invoiceInput = createInvoiceLambda(baseInvoice);
    const invoiceId = invoiceInput.id;

    const invoiceServiceInputs = [];
    const billedOutbounds = [];
    relatedOutbounds.forEach((outbound) => {
      const lineItems = setupOutboundILineItems(
        outbound,
        account.chargeRules,
        account.services,
      );
      // needs invoiceId
      const serviceItems = lineItems.items.map((item, idx) =>
        createInvoiceServiceLambda(invoiceInput, item, idx),
      );

      invoiceServiceInputs.push(...serviceItems);

      if (serviceItems.length > 0) {
        billedOutbounds.push(outbound);
        const outboundUpdateInput = updateLambdaOutbound(
          outbound,
          invoiceInput,
        );

        if (!outboundUpdateMap[invoiceId]) {
          outboundUpdateMap[invoiceId] = [];
        }
        outboundUpdateMap[invoiceId].push(outboundUpdateInput);
      }
    });
    invoiceInput.lineItems = { items: invoiceServiceInputs };

    const shipmentFields = computeAggregatedShipmentFields(billedOutbounds);
    invoiceInput.poNumbers = shipmentFields.poNumbers;
    invoiceInput.railCarNumbers = shipmentFields.railCarNumbers;
    invoiceInput.pickupNumbers = shipmentFields.pickupNumbers;

    const transformedInput = createTransform(invoiceInput, prefix, counter); // TODO: pass isSyncEnabled
    invoiceCreateList.push(transformedInput);
    invoiceServiceCreateMap[invoiceId] = invoiceServiceInputs;
  });

  return {
    invoiceCreateList,
    invoiceServiceCreateMap,
    outboundUpdateMap,
    counter,
  };
}

export const BulkCreateInvoiceButton = (props) => {
  const classes = useStyles();
  const notify = useNotify();
  const redirect = useRedirect();
  const { onSelect, selectedIds } = useListContext();

  const [saving, setSaving] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [mode, setMode] = useState(InvoicingMode.PER_SHIPMENT);
  const [isSyncEnabled, setIsSyncEnabled] = useState(
    getTriggerEnabled("invoiceCreated"),
  );

  useEffect(() => {
    const checkSyncEnabled = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceCreated");
      setIsSyncEnabled(triggerEnabled);
    };
    checkSyncEnabled();
  }, []);

  const isDisabled = isSubscriptionDisabled();

  const handleCreate = async (mode) => {
    try {
      setOpenDialog(false);
      setSaving(true);
      debugger;

      const outboundMap = await fetchOutboundMap(selectedIds);
      const invoicableOutbounds = Object.keys(outboundMap).length;

      if (!invoicableOutbounds) {
        onSelect([]);
        setSaving(false);
        notify("No eligible shipments to invoice.", "warning");
        return;
      }

      const accountMap = await fetchAccountMap(
        ChargeEvent.OUTBOUND,
        outboundMap,
      );
      const reloadYard = await fetchReloadYard();

      const prefix = reloadYard.invoiceNumberPrefix;
      let counter = reloadYard.invoiceCounter;

      let invoiceCreateList, invoiceServiceCreateMap, outboundUpdateMap;

      if (mode === InvoicingMode.PER_SHIPMENT || invoicableOutbounds === 1) {
        ({
          invoiceCreateList,
          invoiceServiceCreateMap,
          outboundUpdateMap,
          counter,
        } = generateInvoicesPerShipment(
          outboundMap,
          accountMap,
          prefix,
          counter,
        ));
      } else {
        ({
          invoiceCreateList,
          invoiceServiceCreateMap,
          outboundUpdateMap,
          counter,
        } = generateInvoicesBulk(outboundMap, accountMap, prefix, counter));
      }

      const oversized = findOversizedInvoice(
        invoiceCreateList,
        invoiceServiceCreateMap,
      );
      if (oversized) {
        setSaving(false);
        notify(
          `Invoice for ${oversized.invoice.accountName} would have ${oversized.lineCount} line items. Max. ${MAX_INVOICE_LINE_ITEMS} per invoice. Reduce your selection.`,
          "warning",
        );
        return; // stop here, don't create
      }

      debugger;
      let reloadYardUpdateInput = null;
      if (counter !== reloadYard.invoiceCounter) {
        reloadYardUpdateInput = {
          id: reloadYard.id,
          active: reloadYard.active,
          name: reloadYard.name,
          invoiceCounter: counter,
        };
      }

      // console.log("invoiceCreateList", invoiceCreateList);
      // console.log("invoiceServiceCreateMap", invoiceServiceCreateMap);
      // console.log("inboundUpdateMap", inboundUpdateMap);
      // console.log("reloadYardUpdateInput", reloadYardUpdateInput);

      const response = await API.post("transREST", "/invoiceCreateBulk", {
        body: {
          invoiceCreateList: invoiceCreateList,
          invoiceServiceCreateMap: invoiceServiceCreateMap,
          inboundUpdateMap: null,
          outboundUpdateMap: outboundUpdateMap,
          reloadYardUpdateInput: reloadYardUpdateInput,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      // Log history for each created invoice
      invoiceCreateList.forEach((invoiceData) => {
        try {
          logInvoiceCreateHistory(invoiceData);

          // trigger zapier event
          if (isSyncEnabled) {
            const items = invoiceServiceCreateMap[invoiceData.id] || [];
            const transformedInvoice = transformZapierInvoiceData(
              {
                ...invoiceData,
                lineItems: { items: items },
              },
              accountMap[invoiceData.accountId]?.services,
            );
            const sanitizedInvoice = sanitizeZapierData(
              syncableInvoiceCreateProps,
              transformedInvoice,
            );
            triggerEvent(sanitizedInvoice, "invoiceCreated")
              .then(() => {
                setInvoiceSyncPending(invoiceData);
              })
              .catch((error) => {
                setInvoiceSyncFailed(invoiceData);
                logError(
                  "outboundShipment:BulkCreateInvoiceButton:triggerEvent",
                  error,
                );
              });
          }
        } catch (error) {
          setInvoiceSyncFailed(invoiceData);
          logError(
            "outboundShipment:BulkCreateInvoiceButton:logInvoiceCreateHistory",
            error,
          );
        }
      });

      const invoiceNum = invoiceCreateList.length;
      notify(
        `${invoiceNum} Invoice${
          invoiceNum === 1 ? "" : "s"
        } created successfully.`,
      );
      redirect("/invoices");
    } catch (err) {
      logError(
        "outboundShipment:BulkCreateInvoiceButton:handleCreate",
        err,
        notify,
        redirect,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={saving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Button
        label="Invoice"
        color="default"
        onClick={handleDialogOpen}
        disabled={isDisabled}
      >
        <AddAllIcon />
      </Button>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Select Invoicing Method</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            aria-label="invoice mode"
          >
            <FormControlLabel
              value={InvoicingMode.PER_SHIPMENT}
              control={<Radio />}
              label="Create 1 invoice per shipment"
            />
            <FormControlLabel
              value={InvoicingMode.BULK}
              control={<Radio />}
              label="Create 1 invoice for all shipments"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" color="default" onClick={handleDialogClose}>
            <CancelIcon />
          </Button>
          <Button
            label="Continue"
            color="primary"
            onClick={() => handleCreate(mode)}
          >
            <OKIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
